<template>
  <section class="container-fluid d-flex mt-5 pt-5 justify-content-center px-3">
    <div class="products-grid container mt-5 rounded-4">
      <div class="container-fluid">
        <!-- Loading State with Skeletons -->
        <div v-if="isLoading || isFiltering">
          <div class="header mb-4">
            <div class="skeleton-title"></div>
          </div>
          
          <div class="row">
            <!-- Skeleton Sidebar - Desktop -->
            <div class="col-lg-3 col-md-4 d-none d-lg-block">
              <div class="skeleton-sidebar">
                <div class="skeleton-search mb-4"></div>
                <div class="skeleton-filters">
                  <div class="skeleton-filter-group" v-for="i in 3" :key="i">
                    <div class="skeleton-filter-title"></div>
                    <div class="skeleton-filter-item" v-for="j in 4" :key="j"></div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Skeleton Sidebar - Mobile -->
            <div class="col-12 d-lg-none">
              <div class="skeleton-mobile-search mb-3"></div>
              <div class="skeleton-mobile-filter-btn mb-4"></div>
            </div>

            <!-- Skeleton Products Grid -->
            <div class="col-lg-9 col-md-8">
              <div class="row g-4">
                <div class="col-xl-4 col-lg-4 col-6" v-for="i in 6" :key="i">
                  <div class="skeleton-card">
                    <div class="skeleton-image"></div>
                    <div class="skeleton-content">
                      <div class="skeleton-text-sm"></div>
                      <div class="skeleton-text-lg"></div>
                      <div class="skeleton-text-md"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Content when loaded -->
        <div v-else>
          <!-- Brand Circles (moved outside of the filtered products block) -->
          <div class="brands-slider position-relative mb-4" :class="{'d-none': isMobile && selectedBrands.length > 0}">
            <!-- Replace existing buttons with new navigation arrows -->
            <div class="brands-navigation-arrows">
              <button 
                class="brands-arrow brands-arrow-left" 
                @click="scrollBrands('left')"
                :class="{'disabled': !canScrollLeft}"
                v-if="!isMobile"
              >
                <i class="fas fa-chevron-left text-primary"></i>
              </button>

              <button 
                class="brands-arrow brands-arrow-right" 
                @click="scrollBrands('right')"
                :class="{'disabled': !canScrollRight}"
                v-if="!isMobile"
              >
                <i class="fas fa-chevron-right text-primary"></i>
              </button>
            </div>
            
            <div class="brands-grid mb-2" ref="brandsContainer">
              <div class="brands-wrapper d-flex d-md-flex flex-wrap flex-md-nowrap gap-3 px-0">
                <!-- All Brands Button -->
                <div class="brand-item" v-if="selectedBrands.length > 0 || !isMobile">
                  <div 
                    class="brand-circle mx-lg-auto mx-0" 
                    :class="{ 'active': selectedBrands.length === 0 }"
                    @click="clearBrandSelection"
                  >
                    <div class="all-brands-icon">
                      <i class="fas fa-globe-americas fa-4x"></i>
                    </div>
                  </div>
                  <span class="brand-name mt-2">Ver todos</span>
                </div>
                
                <!-- Individual Brand Circles -->
                <div v-for="brand in brands" :key="brand.id" class="brand-item mobile-brand-item">
                  <div 
                    class="brand-circle mx-lg-auto mx-0" 
                    :class="{ 'active': selectedBrands.includes(brand.id) }"
                    @click="toggleBrand(brand.id)"
                  >
                    <img 
                      :src="brand.image" 
                      :alt="brand.name"
                      class="brand-image"
                    />
                  </div>
                  <span class="brand-name mt-2">{{ brand.brand_name }}</span>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Selected Brand Info for Mobile -->
          <div v-if="isMobile && selectedBrands.length > 0" class="mb-4 selected-brand-mobile">
            <div class="d-flex align-items-center">
              <button class="btn btn-outline-light btn-sm me-2" @click="clearBrandSelection">
                <i class="fas fa-times me-1"></i> Quitar
              </button>
              <div v-for="brandId in selectedBrands" :key="brandId" class="selected-brand-pill">
                <div class="d-flex align-items-center">
                  <img 
                    :src="getBrandImage(brandId)" 
                    :alt="getBrandName(brandId)"
                    class="selected-brand-image me-2"
                  />
                  <span class="text-white">{{ getBrandName(brandId) }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Título y filtro movidos aquí (después de los círculos) -->
          <div class="d-flex justify-content-between align-items-center mb-4">
            <h1 class="display-5 text-white mb-0 fw-bold">Catálogo<span class="text-primary">.</span></h1>
            <div class="sort-wrapper">
              <select 
                v-model="sortOrder" 
                class="form-select form-select-sm bg-transparent text-white"
                aria-label="Ordenar por precio"
              >
                <option value="">Ordenar por precio</option>
                <option value="low">Menor precio</option>
                <option value="high">Mayor precio</option>
              </select>
            </div>
          </div>

          <div class="row">
            <!-- Filters Sidebar -->
            <div class="col-lg-3 col-md-4">
              <!-- Button for mobile to toggle filters -->
              <div class="position-relative d-md-none d-block">
                <i class="fas fa-search search-icon"></i>
                <input
                  type="text"
                  class="form-control search-input"
                  placeholder="Buscar por nombre"
                  v-model="searchQuery"
                />
              </div>
              <button
                  class="btn btn-outline-light w-100 my-3 d-lg-none"
                  type="button"
                  data-bs-toggle="modal"
                  data-bs-target="#filtersModal"
                >
                <i class="fas fa-filter me-2"></i>
                Mostrar Filtros
              </button>

              <div class="collapse d-lg-block" id="filtersCollapse">
                <div class="search-bar mb-4">
                  <div class="position-relative d-md-block d-none">
                    <i class="fas fa-search search-icon"></i>
                    <input
                      type="text"
                      class="form-control search-input"
                      placeholder="Buscar por nombre"
                      v-model="searchQuery"
                    />
                  </div>
                </div>
                <div class="filters-section">
                  <div class="d-flex justify-content-between align-items-center mb-4">
                    <h5 class="text-white mb-0 fs-5">
                      Filtros <span class="text-muted">({{ totalProducts }})</span>
                    </h5>
                    <button 
                      v-if="hasActiveFilters"
                      @click="clearAllFilters"
                      class="btn btn-link text-white-50 btn-sm"
                    >
                      Limpiar filtros
                    </button>
                  </div>

                  <!-- Ofertas Filter -->
                  <FilterGroup
                    title="Ofertas"
                    icon="fas fa-tag"
                    :options="[{ id: 'discount', name: 'Solo productos en oferta' }]"
                    v-model="discountFilter"
                  />
                  <!-- Stock Filter -->
                  <FilterGroup
                    title="Stock"
                    icon="fas fa-boxes"
                    :options="stockOptions"
                    v-model="selectedStockFilter"
                  />

                  <!-- Type Filter -->
                  <FilterGroup
                    title="Tipo"
                    icon="fas fa-seedling"
                    :options="typeOptions"
                    v-model="selectedTypes"
                  />

                  <!-- National Banks Filter -->
                  <FilterGroup
                    title="Bancos Nacionales"
                    icon="fas fa-flag"
                    :options="nationalBanks"
                    v-model="selectedNationalBrands"
                  />

                  <!-- International Banks Filter -->
                  <FilterGroup
                    title="Bancos Internacionales"
                    icon="fas fa-globe"
                    :options="internationalBanks"
                    v-model="selectedInternationalBrands"
                  />
                </div>
              </div>
            </div>

            <!-- Products Grid -->
            <div class="col-lg-9 col-md-8">
              <div v-if="filteredProducts.length > 0">
                <div class="row g-4">
                  <div
                    class="col-xl-4 col-lg-4 col-6 product-column"
                    v-for="product in filteredProducts"
                    :key="product.id"
                  >
                    <ProductCard :product="product" />
                  </div>
                </div>

                <!-- Load More Button -->
                <div class="text-center mt-5" v-if="hasMoreProducts">
                  <button
                    class="btn btn-outline-light px-5 py-2"
                    @click="loadMore"
                    :disabled="isLoadingMore"
                  >
                    <span v-if="isLoadingMore">
                      <i class="fas fa-spinner fa-spin me-2"></i>Cargando...
                    </span>
                    <span v-else>
                      Cargar más
                    </span>
                  </button>
                </div>
              </div>
              <!-- no products -->
              <div class="text-center h-75 d-flex align-items-center justify-content-center mt-5" v-else>
                <div>
                  <img src="@/assets/icons/cannabis.svg" alt="cannabis plant icon" class="img-fluid mb-4 col-5" />
                  <h3 class="text-white fs-5">No se encontraron productos.</h3>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Filters Modal for Mobile -->
  <div class="modal fade mt-5" id="filtersModal" tabindex="-1" aria-labelledby="filtersModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-fullscreen-sm-down">
      <div class="modal-content bg-dark">
        <div class="modal-header border-secondary">
          <h5 class="modal-title text-white fs-4" id="filtersModalLabel">Filtros</h5>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div class="position-relative mb-4">
            <i class="fas fa-search search-icon"></i>
            <input
              type="text"
              class="form-control search-input"
              placeholder="Buscar por nombre"
              v-model="searchQuery"
            />
          </div>
          <div class="filters-section border-0 p-0">
            <div class="d-flex justify-content-between align-items-center mb-4">
              <h5 class="text-white mb-0 fs-5">
                Filtros <span class="text-muted">({{ totalProducts }})</span>
              </h5>
              <button 
                v-if="hasActiveFilters"
                @click="clearAllFilters"
                class="btn btn-link text-white-50 btn-sm"
              >
                Limpiar filtros
              </button>
            </div>

            <FilterGroup
              title="Ofertas"
              icon="fas fa-tag"
              :options="[{ id: 'discount', name: 'Solo productos en oferta' }]"
              v-model="onlyDiscountedArray"
            />
            <FilterGroup
                    title="Stock"
                    icon="fas fa-boxes"
                    :options="stockOptions"
                    v-model="selectedStockFilter"
                  />

            <FilterGroup
              title="Tipo"
              icon="fas fa-seedling"
              :options="typeOptions"
              v-model="selectedTypes"
            />

            <FilterGroup
              title="Bancos Nacionales"
              icon="fas fa-flag"
              :options="nationalBanks"
              v-model="selectedNationalBrands"
            />

            <FilterGroup
              title="Bancos Internacionales"
              icon="fas fa-globe"
              :options="internationalBanks"
              v-model="selectedInternationalBrands"
            />
          </div>
        </div>
        <div class="modal-footer border-secondary">
          <button type="button" class="btn btn-success w-100 py-3 fw-bold" data-bs-dismiss="modal">
            Aplicar Filtros
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { nextTick } from 'vue'
import ProductCard from "@/components/ProductCard.vue";
import FilterGroup from '@/components/FilterGroup.vue'
import { getProducts, getProductsPaginated } from "@/services/products";
import { getBrands } from "@/services/brands";
import './ProductList.css'; // Import the CSS file

export default {
  name: "ProductsList",
  components: {
    ProductCard,
    FilterGroup
  },
  data() {
    return {
      searchQuery: "",
      selectedSeries: [],
      selectedTypes: [],
      selectedBrands: [],
      selectedNationalBrands: [],
      selectedInternationalBrands: [],
      onlyDiscountedArray: [],
      selectedPriceFilter: [],
      selectedStockFilter: [],
      productsPerPage: 9,
      currentPage: 1,
      products: [],
      totalProductsCount: 0,
      hasNextPage: false,
      isLoadingMore: false,
      brands: [],	
      isMobileLessThan400: window.innerWidth < 400,
      isLoading: true,
      isFiltering: false,
      onlyDiscounted: false,
      sortOrder: '',
      isUpdatingUrl: false,
      isApplyingUrlFilters: false,
      searchTimeout: null,
      typeOptions: [
        { id: "auto", name: "Automáticas" },
        { id: "fem", name: "Feminizadas" },
        { id: "reg", name: "Regulares" },
      ],
      priceOptions: [
        { id: "under-5000", name: "Menos de $5.000" },
        { id: "5000-10000", name: "$5.000 - $10.000" },
        { id: "10000-20000", name: "$10.000 - $20.000" },
        { id: "20000-50000", name: "$20.000 - $50.000" },
        { id: "over-50000", name: "Más de $50.000" },
      ],
      stockOptions: [
        { id: "with-stock", name: "Con stock disponible" },
      ],
      canScrollLeft: false,
      canScrollRight: true,
      previousSelectedBrands: [], // Track previous brand selection
      isMobile: window.innerWidth < 768,
    };
  },
  methods: {
    // Añadir esta función utilitaria al principio de los métodos
    slugify(text) {
      return text
        ?.toString()
        .toLowerCase()
        .trim()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/[\s-]+/g, '-');
    },

    updateCombinedBrands() {
      console.log('🔄 updateCombinedBrands called');
      console.log('National brands:', this.selectedNationalBrands);
      console.log('International brands:', this.selectedInternationalBrands);
      
      const combined = [...this.selectedNationalBrands, ...this.selectedInternationalBrands];
      console.log('Combined brands:', combined);
      
      // Solo actualizar si realmente hay cambios
      if (JSON.stringify(this.selectedBrands) !== JSON.stringify(combined)) {
        this.selectedBrands = combined;
        console.log('✅ selectedBrands updated to:', this.selectedBrands);
      }
    },

    updateSeparatedBrandsFromCombined() {
      console.log('🔄 updateSeparatedBrandsFromCombined called');
      console.log('Combined brands:', this.selectedBrands);
      
      this.selectedNationalBrands = [];
      this.selectedInternationalBrands = [];
      
      for (const brandId of this.selectedBrands) {
        const isNational = this.nationalBanks.some(brand => brand.id === brandId);
        const isInternational = this.internationalBanks.some(brand => brand.id === brandId);
        
        if (isNational) {
          this.selectedNationalBrands.push(brandId);
        } else if (isInternational) {
          this.selectedInternationalBrands.push(brandId);
        }
      }
      
      console.log('✅ Updated separated brands:', {
        national: this.selectedNationalBrands,
        international: this.selectedInternationalBrands
      });
    },

    async loadMore() {
      if (this.isLoadingMore || !this.hasNextPage) return;
      
      this.isLoadingMore = true;
      try {
        const nextPage = this.currentPage + 1;
        const filters = this.getActiveFilters();
        
        const response = await getProductsPaginated(nextPage, this.productsPerPage, filters);
        
        // Agregar los nuevos productos a la lista existente
        this.products = [...this.products, ...response.results];
        this.currentPage = nextPage;
        this.hasNextPage = !!response.next;
        
        console.log(`Cargada página ${nextPage}, productos totales: ${this.products.length}`);
      } catch (error) {
        console.error('Error loading more products:', error);
      } finally {
        this.isLoadingMore = false;
      }
    },
    
    clearAllFilters() {
      this.selectedTypes = []
      this.selectedBrands = []
      this.selectedNationalBrands = []
      this.selectedInternationalBrands = []
      this.selectedPriceFilter = []
      this.selectedStockFilter = []
      this.onlyDiscounted = false
      this.onlyDiscountedArray = []
      this.searchQuery = ''
      this.sortOrder = ''
      this.updateUrlParams()
      // Recargar productos con filtros limpiados (sin mostrar skeleton)
      this.loadProducts(true)
    },
      async applyUrlFilters() {
      if (this.isApplyingUrlFilters) return;
      this.isApplyingUrlFilters = true;
      
      try {
        const { category, categories, bank, banks, search } = this.$route.query;
        
        // Evitar aplicar filtros si no hay query params
        if (!category && !categories && !bank && !banks && !search) {
          this.isApplyingUrlFilters = false;
          return;
        }

        // Reset filters first
        this.selectedTypes = [];
        this.selectedBrands = [];
        this.selectedNationalBrands = [];
        this.selectedInternationalBrands = [];
        this.onlyDiscounted = false;
        this.onlyDiscountedArray = [];
        this.selectedPriceFilter = [];
        this.selectedStockFilter = [];
        this.searchQuery = '';
        
        // Aplicar filtro por categoría
        if (category === 'ofertas') {
          this.onlyDiscounted = true;
          this.onlyDiscountedArray = ['discount'];
        } else if (category) {
          // Compatibilidad con el formato anterior (una sola categoría)
          const categoryMapping = {
            'automaticas': 'auto',
            'feminizadas': 'fem',
            'regulares': 'reg'
          };
          
          const typeId = categoryMapping[category];
          if (typeId) {
            this.selectedTypes = [typeId];
          }
        } else if (categories) {
          // Nuevo formato: múltiples categorías separadas por coma
          const categoryMapping = {
            'automaticas': 'auto',
            'feminizadas': 'fem',
            'regulares': 'reg'
          };
          
          const categoryList = categories.split(',');
          const typeIds = categoryList
            .map(cat => categoryMapping[cat.trim()])
            .filter(Boolean);
          
          if (typeIds.length > 0) {
            this.selectedTypes = typeIds;
          }
        }

        // Aplicar filtro por banco(s)
        if (banks && this.brands.length > 0) {
          // Nuevo formato: múltiples bancos separados por coma
          const bankSlugs = banks.split(',').map(b => b.trim().toLowerCase());
          const selectedBankIds = [];
          
          for (const bankSlug of bankSlugs) {
            const bankInfo = this.brands.find(b => 
              this.slugify(b.brand_name).toLowerCase() === bankSlug
            );
            
            if (bankInfo) {
              selectedBankIds.push(bankInfo.id);
              console.log(`Banco encontrado: ${bankInfo.brand_name}, ID: ${bankInfo.id}`);
            }
          }
          
          if (selectedBankIds.length > 0) {
            this.selectedBrands = selectedBankIds;
            this.previousSelectedBrands = [...this.selectedBrands]; // Save the state
            
            // También actualizar las variables separadas
            this.updateSeparatedBrandsFromCombined();
          }
        } else if (bank && this.brands.length > 0) {
          // Compatibilidad con el formato anterior (un solo banco)
          const bankSlug = bank.toLowerCase();
          const bankInfo = this.brands.find(b => 
            this.slugify(b.brand_name).toLowerCase() === bankSlug
          );
          
          if (bankInfo) {
            this.selectedBrands = [bankInfo.id];
            this.previousSelectedBrands = [...this.selectedBrands]; // Save the state
            
            // También actualizar las variables separadas
            this.updateSeparatedBrandsFromCombined();
            
            console.log(`Banco encontrado: ${bankInfo.brand_name}, ID: ${bankInfo.id}`);
          } else {
            console.warn(`Banco no encontrado para slug: ${bank}`);
            console.log('Marcas disponibles:', this.brands.map(b => ({ 
              name: b.brand_name, 
              slug: this.slugify(b.brand_name),
              id: b.id 
            })));
          }
        }

        // Aplicar búsqueda
        if (search) {
          this.searchQuery = decodeURIComponent(search);
        }
        
        // Recargar productos con los nuevos filtros aplicados
        await this.loadProducts(true);
      } finally {
        this.isApplyingUrlFilters = false;
      }
    },
    
    findBankId(bankSlug) {
      if (!bankSlug) return null;
      
      // Convertir a minúsculas para comparación case-insensitive
      const slug = bankSlug.toLowerCase();
      
      // Buscar primero en marcas completas
      const brand = this.brands.find(b => this.slugify(b.brand_name).toLowerCase() === slug);
      if (brand) return brand.id;
      
      // Si no se encuentra, buscar en bancos nacionales/internacionales
      const allBanks = [...this.nationalBanks, ...this.internationalBanks];
      const bank = allBanks.find(b => this.slugify(b.name).toLowerCase() === slug);
      return bank?.id;
    },
      updateUrlParams() {
      // Prevent multiple rapid updates
      if (this.isUpdatingUrl) return;
      this.isUpdatingUrl = true;

      try {
        const query = {};
        
        if (this.onlyDiscounted) {
          query.category = 'ofertas';
        } else if (this.selectedTypes.length > 0) {
          const typeMapping = {
            'auto': 'automaticas',
            'fem': 'feminizadas',
            'reg': 'regulares'
          };
          // Manejar múltiples tipos seleccionados
          const categoryValues = this.selectedTypes.map(type => typeMapping[type]).filter(Boolean);
          if (categoryValues.length > 0) {
            query.categories = categoryValues.join(',');
          }
        }
        
        // Manejar múltiples bancos seleccionados
        if (this.selectedBrands.length > 0) {
          const selectedBanksInfo = [];
          
          for (const brandId of this.selectedBrands) {
            const selectedBrand = this.brands.find(b => b.id === brandId);
            if (selectedBrand) {
              selectedBanksInfo.push(this.slugify(selectedBrand.brand_name).toLowerCase());
            }
          }
          
          if (selectedBanksInfo.length > 0) {
            query.banks = selectedBanksInfo.join(',');
            console.log(`Actualizando URL con bancos: ${selectedBanksInfo.join(', ')}`);
            
            // Update previous selected brands
            this.previousSelectedBrands = [...this.selectedBrands];
          }
        } else if (this.selectedBrands.length === 0 && this.previousSelectedBrands.length > 0) {
          // If previously there was a selection and now it's empty, remove banks from URL
          delete query.banks;
          this.previousSelectedBrands = [];
        }        if (this.searchQuery) {
          query.search = encodeURIComponent(this.searchQuery);
        }
        
        // Para mantener retrocompatibilidad con el formato anterior
        // Si solo hay una categoría y no está usando el nuevo formato, usar el viejo
        if (query.categories && query.categories.split(',').length === 1) {
          query.category = query.categories;
          delete query.categories;
        }
        
        // Si solo hay un banco y no está usando el nuevo formato, usar el viejo
        if (query.banks && query.banks.split(',').length === 1) {
          query.bank = query.banks;
          delete query.banks;
        }

        // Verify if URL actually needs updating to prevent recursion
        const currentQueryStr = JSON.stringify(this.$route.query);
        const newQueryStr = JSON.stringify(query);
        
        if (currentQueryStr !== newQueryStr) {
          this.$router.replace({ query }).catch(() => {});
        }
      } finally {
        // Asegurarse de que isUpdatingUrl se restablece después de un tiempo
        setTimeout(() => {
          this.isUpdatingUrl = false;
        }, 300);
      }
    },
    
    getActiveFilters() {
      const filters = {};
      
      if (this.selectedTypes.length > 0) {
        filters.type = this.selectedTypes;
      }
      
      if (this.selectedBrands.length > 0) {
        filters.brands = this.selectedBrands;
      }
      
      if (this.searchQuery) {
        filters.search = this.searchQuery;
      }
      
      if (this.onlyDiscounted) {
        filters.onlyDiscounted = true;
      }
      
      if (this.selectedPriceFilter.length > 0) {
        filters.priceRange = this.selectedPriceFilter;
      }
      
      if (this.selectedStockFilter.length > 0) {
        // Separar el filtro "Sin stock" de otros filtros de stock
        const outOfStockFilter = this.selectedStockFilter.includes('out-of-stock');
        const otherStockFilters = this.selectedStockFilter.filter(filter => filter !== 'out-of-stock');
        
        if (outOfStockFilter) {
          filters.noStock = true;
        }
        if (otherStockFilters.length > 0) {
          filters.stockFilter = otherStockFilters;
        }
      }
      
      if (this.sortOrder) {
        const orderingMap = {
          'low': 'min_price',
          'high': '-min_price'
        };
        filters.ordering = orderingMap[this.sortOrder];
      }
      
      return filters;
    },

    async loadProducts(resetProducts = true, showMainLoader = false) {
      // Solo mostrar el loader principal en la carga inicial
      if (showMainLoader) {
        this.isLoading = true;
      } else if (resetProducts) {
        // Mostrar skeleton de filtrado cuando se reinician los productos (filtros aplicados)
        this.isFiltering = true;
      }
      
      try {
        if (resetProducts) {
          this.products = [];
          this.currentPage = 1;
        }
        
        const filters = this.getActiveFilters();
        console.log('LoadProducts called with filters:', filters);
        console.log('Selected brands:', this.selectedBrands);
        
        const response = await getProductsPaginated(this.currentPage, this.productsPerPage, filters);
        console.log('Products response received:', response);
        
        if (resetProducts) {
          this.products = response.results;
        } else {
          this.products = [...this.products, ...response.results];
        }
        
        this.totalProductsCount = response.count;
        this.hasNextPage = !!response.next;
        
        console.log('Products loaded:', this.products.length, 'of', this.totalProductsCount);
      } catch (error) {
        console.error('Error loading products:', error);
        // Fallback al método anterior si falla la paginación
        try {
          this.products = await getProducts();
          this.totalProductsCount = this.products.length;
          this.hasNextPage = false;
        } catch (fallbackError) {
          console.error('Error loading products with fallback:', fallbackError);
        }
      } finally {
        if (showMainLoader) {
          this.isLoading = false;
        } else {
          this.isFiltering = false;
        }
      }
    },

    async loadBrands() {
      try {
        this.brands = await getBrands();
        console.log('Marcas cargadas:', this.brands.length);
      } catch (error) {
        console.error('Error loading brands:', error);
      }
    },

    async loadAllProductsForBrands() {
      try {
        // Ya no es necesario cargar todos los productos para las marcas
        // Las marcas se muestran desde la API de brands directamente
        // Solo actualizamos las marcas con información básica
        this.brands = this.brands.map(brand => ({
          ...brand,
          hasNationalProducts: true, // Asumir que todas las marcas tienen productos
          hasInternationalProducts: true,
          productCount: 0 // Se actualizará dinámicamente cuando sea necesario
        }));
      } catch (error) {
        console.error('Error loading brands info:', error);
      }
    },

    scrollBrands(direction) {
      const container = this.$refs.brandsContainer;
      if (!container) return;
      
      const scrollAmount = container.clientWidth * 0.75;
      if (direction === 'left') {
        container.scrollBy({
          left: -scrollAmount,
          behavior: 'smooth'
        });
      } else {
        container.scrollBy({
          left: scrollAmount,
          behavior: 'smooth'
        });
      }
      
      // Update buttons after animation completes
      setTimeout(() => {
        this.updateScrollButtons();
      }, 500);
    },

    updateScrollButtons() {
      const container = this.$refs.brandsContainer;
      if (!container) return;
      
      this.canScrollLeft = container.scrollLeft > 0;
      this.canScrollRight = container.scrollLeft < (container.scrollWidth - container.clientWidth);
    },

    setupScrollListeners() {
      const container = this.$refs.brandsContainer;
      if (!container) return;
      
      container.addEventListener('scroll', this.updateScrollButtons);
      this.updateScrollButtons();

      // Detect mobile screen size
      this.checkIfMobile();
      window.addEventListener('resize', this.checkIfMobile);
    },

    removeScrollListeners() {
      const container = this.$refs.brandsContainer;
      if (!container) return;
      
      container.removeEventListener('scroll', this.updateScrollButtons);
      window.removeEventListener('resize', this.checkIfMobile);
    },

    checkIfMobile() {
      this.isMobile = window.innerWidth < 768;
      this.$nextTick(() => {
        this.updateScrollButtons();
      });
    },
    
    getBrandImage(brandId) {
      const brand = this.brands.find(b => b.id === brandId);
      return brand ? brand.image : '';
    },
    
    getBrandName(brandId) {
      const brand = this.brands.find(b => b.id === brandId);
      return brand ? brand.brand_name : '';
    },

    clearBrandSelection() {
      this.selectedBrands = [];
      this.selectedNationalBrands = [];
      this.selectedInternationalBrands = [];
      this.updateUrlParams();
      this.loadProducts(true);
    },
    
    // Modificar el método toggleBrand para permitir múltiples selecciones
    toggleBrand(brandId) {
      console.log(`Toggle brand: ${brandId}, currently selected: ${this.selectedBrands}`);
      console.log(`Available brands:`, this.brands);
      
      // Save current state before changing
      this.previousSelectedBrands = [...this.selectedBrands];
      
      // Encontrar si es banco nacional o internacional
      const isNational = this.nationalBanks.some(brand => brand.id === brandId);
      const isInternational = this.internationalBanks.some(brand => brand.id === brandId);
      
      if (isNational) {
        const index = this.selectedNationalBrands.indexOf(brandId);
        if (index === -1) {
          this.selectedNationalBrands.push(brandId);
          console.log(`National brand ${brandId} added to selection`);
        } else {
          this.selectedNationalBrands.splice(index, 1);
          console.log(`National brand ${brandId} removed from selection`);
        }
      } else if (isInternational) {
        const index = this.selectedInternationalBrands.indexOf(brandId);
        if (index === -1) {
          this.selectedInternationalBrands.push(brandId);
          console.log(`International brand ${brandId} added to selection`);
        } else {
          this.selectedInternationalBrands.splice(index, 1);
          console.log(`International brand ${brandId} removed from selection`);
        }
      }
      
      // No necesitamos llamar a updateCombinedBrands aquí porque los watchers lo harán
    },
  },
  async created() {
    try {
      await this.loadBrands();
      await this.loadAllProductsForBrands(); // Solo cargar información básica de marcas
      await this.loadProducts(true, true); // Mostrar loader solo en carga inicial
      
      // Apply URL filters only after both brands and products are loaded
      if (Object.keys(this.$route.query).length > 0) {
        // Marcar como actualizando URL para evitar reemplazos inmediatos
        this.isUpdatingUrl = true;
        await this.applyUrlFilters();
        setTimeout(() => {
          this.isUpdatingUrl = false;
        }, 500);
      }
      
      document.title = "Catálogo de semillas - Semillero del oeste";
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      this.isLoading = false;
    }
  },
  async mounted() {
    await nextTick();
    this.setupScrollListeners();
  },
  beforeUnmount() {
    this.removeScrollListeners();
    // Limpiar timeout de búsqueda
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
  watch: {
    selectedNationalBrands: {
      handler(newVal) {
        console.log('🏛️ selectedNationalBrands watcher triggered:', newVal);
        this.updateCombinedBrands();
      },
      deep: true,
      immediate: false
    },
    selectedInternationalBrands: {
      handler(newVal) {
        console.log('🌍 selectedInternationalBrands watcher triggered:', newVal);
        this.updateCombinedBrands();
      },
      deep: true,
      immediate: false
    },
    onlyDiscountedArray: {
      handler(newVal) {
        console.log('💰 onlyDiscountedArray watcher triggered:', newVal);
        this.onlyDiscounted = newVal.includes('discount');
      },
      deep: true,
      immediate: false
    },
    selectedTypes: {
      handler(newVal, oldVal) {
        console.log('🏷️ selectedTypes watcher triggered:', { 
          newVal, 
          oldVal,
          isUpdatingUrl: this.isUpdatingUrl,
          isApplyingUrlFilters: this.isApplyingUrlFilters 
        });
        
        if (!this.isUpdatingUrl && !this.isApplyingUrlFilters) {
          console.log('selectedTypes changed, updating products...');
          this.updateUrlParams();
          this.loadProducts(true); // Sin mostrar skeleton
        }
      },
      deep: true,
      immediate: false
    },
    selectedBrands: {
      handler(newVal, oldVal) {
        console.log('🏢 selectedBrands watcher triggered:', { 
          newVal, 
          oldVal,
          isUpdatingUrl: this.isUpdatingUrl,
          isApplyingUrlFilters: this.isApplyingUrlFilters,
          hasChanged: JSON.stringify(newVal) !== JSON.stringify(oldVal)
        });
        
        if (!this.isUpdatingUrl && !this.isApplyingUrlFilters && JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
          console.log('selectedBrands changed, updating products...');
          this.updateUrlParams();
          this.loadProducts(true); // Sin mostrar skeleton
        }
      },
      deep: true,
      immediate: false
    },
    searchQuery: {
      handler(newVal, oldVal) {
        console.log('🔍 searchQuery watcher triggered:', { 
          newVal, 
          oldVal,
          isUpdatingUrl: this.isUpdatingUrl,
          isApplyingUrlFilters: this.isApplyingUrlFilters 
        });
        
        if (!this.isUpdatingUrl && !this.isApplyingUrlFilters) {
          this.updateUrlParams();
          // Debounce la búsqueda para evitar demasiadas llamadas
          clearTimeout(this.searchTimeout);
          this.searchTimeout = setTimeout(() => {
            this.loadProducts(true); // Sin mostrar skeleton
          }, 500);
        }
      },
      immediate: false
    },
    onlyDiscounted: {
      handler(newVal, oldVal) {
        console.log('💰 onlyDiscounted watcher triggered:', { 
          newVal, 
          oldVal,
          isUpdatingUrl: this.isUpdatingUrl,
          isApplyingUrlFilters: this.isApplyingUrlFilters 
        });
        
        if (!this.isUpdatingUrl && !this.isApplyingUrlFilters) {
          console.log('onlyDiscounted changed, updating products...');
          this.updateUrlParams();
          this.loadProducts(true); // Sin mostrar skeleton
        }
      },
      immediate: false
    },
    sortOrder: {
      handler(newVal, oldVal) {
        console.log('📊 sortOrder watcher triggered:', { 
          newVal, 
          oldVal,
          isUpdatingUrl: this.isUpdatingUrl,
          isApplyingUrlFilters: this.isApplyingUrlFilters 
        });
        
        if (!this.isUpdatingUrl && !this.isApplyingUrlFilters) {
          console.log('sortOrder changed, updating products...');
          this.loadProducts(true); // Sin mostrar skeleton
        }
      },
      immediate: false
    },
    selectedPriceFilter: {
      handler(newVal, oldVal) {
        console.log('💲 selectedPriceFilter watcher triggered:', { 
          newVal, 
          oldVal,
          isUpdatingUrl: this.isUpdatingUrl,
          isApplyingUrlFilters: this.isApplyingUrlFilters 
        });
        
        if (!this.isUpdatingUrl && !this.isApplyingUrlFilters) {
          console.log('priceFilter changed, updating products...');
          this.updateUrlParams();
          this.loadProducts(true); // Sin mostrar skeleton
        }
      },
      deep: true,
      immediate: false
    },
    selectedStockFilter: {
      handler(newVal, oldVal) {
        console.log('📦 selectedStockFilter watcher triggered:', { 
          newVal, 
          oldVal,
          isUpdatingUrl: this.isUpdatingUrl,
          isApplyingUrlFilters: this.isApplyingUrlFilters 
        });
        
        if (!this.isUpdatingUrl && !this.isApplyingUrlFilters) {
          console.log('stockFilter changed, updating products...');
          this.updateUrlParams();
          this.loadProducts(true); // Sin mostrar skeleton
        }
      },
      deep: true,
      immediate: false
    }
  },
  computed: {
    nationalBanks() {
      return this.brands
        .filter(brand => brand.is_national === true)
        .map(brand => ({
          id: brand.id,
          name: brand.brand_name,
          image: brand.image,
          count: brand.productCount || 0
        }));
    },
    internationalBanks() {
      return this.brands
        .filter(brand => brand.is_national === false)
        .map(brand => ({
          id: brand.id,
          name: brand.brand_name,
          image: brand.image,
          count: brand.productCount || 0
        }));
    },
    filteredProducts() {
      // Con la nueva paginación, los productos ya vienen filtrados del servidor
      // Solo necesitamos aplicar el filtrado local si es necesario
      return this.products;
    },
    totalProducts() {
      return this.totalProductsCount;
    },
    hasMoreProducts() {
      return this.hasNextPage;
    },
    hasActiveFilters() {
      return this.selectedTypes.length > 0 || 
             this.selectedBrands.length > 0 || 
             this.onlyDiscounted ||
             this.searchQuery.length > 0
    },
    discountFilter: {
      get() {
        return this.onlyDiscounted ? ['discount'] : []
      },
      set(value) {
        this.onlyDiscounted = value.includes('discount')
      }
    },
    uniqueBrands() {
      const allBanks = [...this.nationalBanks, ...this.internationalBanks];
      return allBanks.sort((a, b) => a.name.localeCompare(b.name));
    },
    // Modificar la computed property para los círculos de marcas
    allBrands() {
      return this.brands.map(brand => ({
        id: brand.id,
        name: brand.brand_name,
        image: brand.image
      }));
    },
  }
};
</script>

<style scoped>
/* Styles moved to ProductList.css */
/* Añadir estilos específicos para la vista grid en móvil */
@media (max-width: 767px) {
  .brands-wrapper {
    display: grid !important;
    grid-template-columns: repeat(2, 1fr) !important;
    grid-auto-rows: auto !important;
    gap: 1.5rem 1rem !important;
    width: 100%;
    overflow-x: hidden !important;
    padding: 0.5rem;
  }
  
  .mobile-brand-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
  }
  
  .brands-slider .btn-scroll {
    display: none !important;
  }
  
  .brands-grid {
    overflow-x: visible !important;
    padding: 0;
  }
  
  .brand-item {
    margin-bottom: 0.5rem;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  
  .brand-name {
    text-align: center;
    max-width: 100%;
    white-space: normal;
    overflow: hidden;
    text-overflow: ellipsis;
    word-wrap: break-word;
    max-height: 40px;
    font-size: 0.8rem;
    margin-top: 0.75rem !important;
  }
  
  .brand-circle {
    width: 140px !important;
    height: 140px !important;
    min-width: 110px !important;
    min-height: 110px !important;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 auto;
    overflow: visible !important;
    position: relative;
    padding: 0 !important;
    border-radius: 50%;
  }
  
  .brand-circle img {
    max-width: 85% !important;
    max-height: 85% !important;
    width: auto !important;
    height: auto !important;
    object-fit: contain;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border-radius: 0;
  }
  
  .selected-brand-mobile {
    background-color: rgba(255,255,255,0.05);
    border-radius: 8px;
    padding: 10px;
  }
  
  .selected-brand-pill {
    background-color: rgba(0,0,0,0.3);
    border-radius: 20px;
    padding: 5px 10px;
    margin-right: 5px;
  }
  
  .selected-brand-image {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    object-fit: cover;
  }
  
  .all-brands-icon i {
    font-size: 2rem !important;
  }
}

@media (max-width: 400px) {
  .brands-wrapper {
    grid-template-columns: repeat(2, 1fr) !important;
    gap: 1rem !important;
  }
  
  .brand-circle {
    width: 90px !important;
    height: 90px !important;
    min-width: 90px !important;
    min-height: 90px !important;
  }
  
  .brand-circle img {
    max-width: 70px !important;
    max-height: 70px !important;
  }
  
  .all-brands-icon i {
    font-size: 1.8rem !important;
  }
}

/* Mejorar la visibilidad del botón del modal en dispositivos móviles */
.modal-footer .btn-success {
  background-color: #28a745;
  font-size: 1.1rem;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
}

.modal-footer .btn-success:hover,
.modal-footer .btn-success:active {
  background-color: #218838;
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.3);
}

@media (max-width: 767px) {
  .modal-content {
    border: none;
    border-radius: 16px;
  }
  
  .modal-footer {
    padding: 1rem;
    margin-bottom: 2.5rem;
  }
  
  .modal-header {
    padding: 1.2rem 1rem;
  }
}
</style>
